/** @jsxImportSource @emotion/react */
import PropTypes from "prop-types";
import _ from "lodash";
import styled from "@emotion/styled";
import { FlexColDiv, FlexDiv } from "../../../styles/container-elements";
import LinkButton from "../../../vendor/signal-widgets/components/link-button";
import {
  LadChicletCSS as Chiclet,
  LadMilestoneChiclet,
  colorsForLad,
} from "../../../components/chiclets";
import { useTranslation } from "react-i18next";
import { FontSize, Text } from "components/atoms/Text.atom";
import { MapPreference } from "shared/constants/organization.const";
import { DateTime } from "components/atoms/DateTime.atom";
import { getCurrentVehicleLocation } from "components-old/CoordinatesTable";
import { TimeCell } from "components/organisms/base-table/Cell/TimeCell";

const formattedStartCase = (str) => (str ? _.startCase(_.toLower(str)) : "");

const nameCss = {
  color: "#151724",
  fontSize: "larger",
  fontWeight: "bold",
  marginLeft: "1em",
  lineHeight: "1.5em",
  padding: 0,
  textAlign: "left",
  width: "70%",
};

const Addr = styled.div({
  color: "#a1a1a1",
  lineHeight: "1em",
  marginTop: ".5em",
});

const DetailRow = styled.div({
  display: "flex",
  marginTop: "0.5em",
  "& strong": {
    minWidth: "100px",
    display: "inline-block",
  },
});

export const LocationInfoPopup = ({ data, lad, type, eventHandler }) => {
  const { t } = useTranslation("fv-vin-details");
  const isMilestone = data.code && data.codeDescription;
  const col = colorsForLad(lad);

  const renderLocationContent = () => {
    const formattedName = formattedStartCase(data.name);
    const nameComponent = eventHandler ? (
      <LinkButton
        css={nameCss}
        onClick={() => eventHandler(data, "LOCATION_LINK")}
      >
        {formattedName}
      </LinkButton>
    ) : (
      <div css={nameCss}>{formattedName}</div>
    );

    return (
      <>
        <FlexDiv css={{ display: "inline-flex" }}>
          <Chiclet height={20} width={20} lad={lad} />
          {nameComponent}
        </FlexDiv>
        <Addr>{formattedStartCase(data.address)}</Addr>
        <Addr>{`${formattedStartCase(data.city)}, ${data.state}`}</Addr>
      </>
    );
  };
  const renderMilestoneContent = () => (
    <>
      <FlexDiv css={{ display: "inline-flex", alignItems: "center" }}>
        <LadMilestoneChiclet
          height={20}
          width={20}
          milestoneCode={data.code}
          milestoneNumber={data.code}
        />
        <div css={nameCss}>{data.code}</div>
      </FlexDiv>
      <DetailRow>
        <Text size={FontSize.size12}>{data.codeDescription}</Text>
      </DetailRow>
      {data.locationCode && (
        <DetailRow>
          <strong>{t("fv-vin-details:Location Code")}:</strong>
          <Text size={FontSize.size12}>{data.locationCode}</Text>
        </DetailRow>
      )}
      {data.eventTs && (
        <FlexDiv style={{ gap: "1em" }}>
          <Text size={FontSize.size12}>{t("fv-vin-details:Event Time")}</Text>
          <TimeCell dateTime={data?.eventTs} localize />
        </FlexDiv>
      )}
      {data.ts && (
        <FlexDiv style={{ gap: "1em" }}>
          <Text size={FontSize.size12}>
            {t("fv-vin-details:Received Time")}
          </Text>
          <TimeCell dateTime={data?.ts} localize />
        </FlexDiv>
      )}
    </>
  );

  const renderConnectedCarContent = () => {
    const fontSize = FontSize.size12;
    return (
      <>
        <FlexDiv>
          <DetailRow>
            <Text size={fontSize} bold>
              {t("fv-vin-details:Event Time")}:{" "}
            </Text>
            <DateTime plain localize dateTime={data.time} size={fontSize}>
              <DateTime.Time bold size={FontSize.size12} />
              <DateTime.Timezone bold size={FontSize.size12} />
            </DateTime>
          </DetailRow>
        </FlexDiv>
        <FlexDiv>
          <DetailRow>
            <Text size={fontSize} bold>
              {t("fv-vin-details:Received Time")}:{" "}
            </Text>
            <DateTime plain localize dateTime={data.db_time} size={fontSize}>
              <DateTime.Time size={FontSize.size12} />
              <DateTime.Timezone size={FontSize.size12} />
            </DateTime>
          </DetailRow>
        </FlexDiv>
        <FlexDiv>
          <Text size={fontSize} bold>
            {t("fv-vin-details:Latitude")}:{" "}
          </Text>
          <Text size={fontSize}>{data.latitude}</Text>
        </FlexDiv>
        <FlexDiv>
          <Text size={fontSize} bold>
            {t("fv-vin-details:Longitude")}:{" "}
          </Text>
          <Text size={fontSize}>{data.longitude}</Text>
        </FlexDiv>
        <FlexColDiv>
          <Text size={fontSize} bold>
            {t("fv-vin-details:Current Location")}:{" "}
          </Text>
          <Text size={fontSize}>{getCurrentVehicleLocation(t, data)}</Text>
        </FlexColDiv>
        <FlexDiv>
          <Text size={fontSize} bold>
            {t("fv-vin-details:City, State")}:{" "}
          </Text>
          <Text size={fontSize}>
            {data.city}, {data.state}
          </Text>
        </FlexDiv>
      </>
    );
  };

  return (
    <FlexColDiv
      css={{
        backgroundColor: "white",
        border: `4px solid ${col.primary}`,
        borderRadius: 4,
        padding: ".5em",
        fontSize: "x-small",
        width: isMilestone ? "18em" : "22em",
      }}
    >
      {isMilestone
        ? renderMilestoneContent()
        : type === MapPreference.CONNECTED_CAR
        ? renderConnectedCarContent()
        : renderLocationContent()}
      <div
        className="locinfowindow-tip"
        css={{
          borderLeft: "12px solid transparent",
          borderRight: "12px solid transparent",
          borderTop: `12px solid ${col.primary}`,
          bottom: -10,
          height: 0,
          position: "absolute",
          right: 11,
          width: 0,
        }}
      />
    </FlexColDiv>
  );
};

LocationInfoPopup.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    code: PropTypes.string,
    codeDescription: PropTypes.string,
    eventTs: PropTypes.string,
    ts: PropTypes.string,
    comments: PropTypes.string,
    milestone1: PropTypes.shape({
      description: PropTypes.string,
      eventTs: PropTypes.string,
      comments: PropTypes.string,
    }),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    db_time: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
  lad: PropTypes.object.isRequired,
  eventHandler: PropTypes.func,
  type: PropTypes.string,
};
